import Phaser from 'phaser';
import type { Intents } from '../../sim/simulation';
import { TouchControls } from './TouchControls';

/**
 * Merges touch and keyboard into the sim's intents, one sample per tick. Keyboard: arrows or A/D
 * to move, Space/W/Up to jump, J or X to fire (hold, then release: rocket), Shift to dash, Q
 * (farther) and E (nearer) to switch lanes.
 */
export class InputController {
  readonly touch: TouchControls;
  private readonly keys: Record<
    'left' | 'right' | 'a' | 'd' | 'space' | 'w' | 'up' | 'j' | 'x',
    Phaser.Input.Keyboard.Key
  > | null;
  private readonly laneKeys: (-1 | 1)[] = [];
  private jumpTapped = false;
  private fireTapped = false;
  private dashTapped = false;
  /** Off while the game is paused: presses then are dropped, not saved for later. */
  private enabled = true;

  constructor(scene: Phaser.Scene) {
    this.touch = new TouchControls(scene);
    const kb = scene.input.keyboard;
    if (kb) {
      const K = Phaser.Input.Keyboard.KeyCodes;
      this.keys = kb.addKeys({
        left: K.LEFT,
        right: K.RIGHT,
        a: K.A,
        d: K.D,
        space: K.SPACE,
        w: K.W,
        up: K.UP,
        j: K.J,
        x: K.X,
      }) as InputController['keys'];
      // Phaser can dispatch the same queued KeyboardEvent again on the next frame (its duplicate
      // check only compares with the previous event, so a keydown followed by its keyup slips
      // through). Seen as one Q press switching two lanes; each event is handled once here.
      const handled = new WeakSet<KeyboardEvent>();
      const once = (action: () => void) => (event: KeyboardEvent) => {
        if (handled.has(event)) return;
        handled.add(event);
        action();
      };
      const press = (key: string, action: () => void) =>
        kb.on(
          `keydown-${key}`,
          once(() => {
            if (this.enabled) action();
          }),
        );
      press('Q', () => this.laneKeys.push(1));
      press('E', () => this.laneKeys.push(-1));
      press('SHIFT', () => (this.dashTapped = true));
      for (const key of ['SPACE', 'W', 'UP']) press(key, () => (this.jumpTapped = true));
      for (const key of ['J', 'X']) press(key, () => (this.fireTapped = true));
    } else {
      this.keys = null;
    }
  }

  /** Turns input on or off (the pause menu), dropping anything pressed or held so far. */
  setEnabled(on: boolean): void {
    this.enabled = on;
    this.touch.setEnabled(on);
    this.laneKeys.length = 0;
    this.jumpTapped = false;
    this.fireTapped = false;
    this.dashTapped = false;
  }

  /** What the player wants this tick. Clears one-shot presses. */
  sample(): Intents {
    const k = this.keys;
    const keyMove = k
      ? (k.right.isDown || k.d.isDown ? 1 : 0) - (k.left.isDown || k.a.isDown ? 1 : 0)
      : 0;
    const keyJump = k ? k.space.isDown || k.w.isDown || k.up.isDown : false;
    const keyFire = k ? k.j.isDown || k.x.isDown : false;
    const t = this.touch;
    const intents: Intents = {
      moveX: t.moveX !== 0 ? t.moveX : keyMove,
      jump: t.jumpHeld || t.jumpTapped || keyJump || this.jumpTapped,
      fire: t.fireHeld || t.fireTapped || keyFire || this.fireTapped,
      dash: t.dashTapped || this.dashTapped,
      lane: this.laneKeys.shift() ?? t.takeLane(),
    };
    t.jumpTapped = false;
    t.fireTapped = false;
    t.dashTapped = false;
    this.dashTapped = false;
    this.jumpTapped = false;
    this.fireTapped = false;
    return intents;
  }
}
